<template>
	<section class="deal-checklist-plaid-link stretch-to-fit">
		<PlaidLink
			v-if="!useAccountSelect"
			class="stretch-to-fit"
			:linkConfig="plaidConfig"
			:receivedRedirectUrl="receivedRedirectUrl"
			disableStyling
			@isBusyUpdate="onPlaidBusyUpdate"
			@success="onUploadComplete"
			@exit="onPlaidExit"
			@error="onPlaidError"
		>
			<template #prelaunchScreen>
				<h2>{{ checklistItem.name }}</h2>
				<p>{{ checklistItem.description }}</p>

				<p
					v-if="resetWarning"
					class="warning-message"
				>
					{{ resetWarning }}
				</p>
			</template>
			<template #failureScreen>
				<div class="failure-message">
					<h2>{{ failureMessage }}</h2>
					<br />
					<i class="fas fa-times" />
					<button
						class="button"
						@click="$emit('exitItem')"
					>
						Return to Checklist
					</button>
				</div>
			</template>
			<template #successScreen>
				<div class="success-message">
					<h2>{{ successMessage }}</h2>
					<br />
					<i class="fas fa-check" />
				</div>
				<button
					class="button"
					@click="$emit('exitItem')"
				>
					Return to Checklist
				</button>
			</template>
		</PlaidLink>

		<div
			v-else
			class="stretch-to-fit"
		>
			<section
				v-show="!isAccountSelectDone && !isBusy"
				class="plaid-account-select-container stretch-to-fit"
			>
				<div>
					<h2>{{ checklistItem.name }}</h2>
					<p>{{ checklistItem.description }}</p>

					<PlaidAccountSelect
						v-if="plaidCustomerInfo"
						:plaidCustomerData="plaidCustomerInfo"
						:plaidProductFilter="productList"
						:enableMultiSelect="useAccountMultiSelect"
						enableBankSelect
						disableStyling
						@launchLinkUpdate="launchLinkInUpdateMode"
						@selectedAccountUpdate="selectedAccountUpdate"
					/>

					<PlaidLink
						ref="plaidLink"
						:linkConfig="plaidConfig"
						storeCode="UNKNOWN"
						hideConnectedBanks
						disableStyling
						@isBusyUpdate="onPlaidBusyUpdate"
						@plaidCustomerUpdate="plaidLinkCustomerUpdate"
						@exit="onPlaidExit"
						@error="onPlaidError"
					/>
				</div>

				<button
					class="button button-account-select"
					:disabled="selectedAccounts.length < 1"
					@click="onAccountsSelected"
				>
					{{ useAccountMultiSelect ? 'Select Accounts' : 'Select Account' }}
				</button>
			</section>

			<section
				v-show="isAccountSelectDone && !isBusy"
				class="plaid-account-select-post stretch-to-fit"
			>
				<div class="success-message">
					<h2>{{ successMessage }}</h2>
					<br />
					<i class="fas fa-check" />
				</div>
				<button
					class="button"
					@click="$emit('exitItem')"
				>
					Return to Checklist
				</button>
			</section>
		</div>

		<LoaderComponent v-if="isPlaidBusy || isBusy" />
	</section>
</template>

<script>
	import api from '@core/services/api/_api';
	import ENUMS from '@core/classes/Enums';
	import DealChecklistItem from '@core/classes/Checklist/DealChecklistItem';
	import DocumentAssociation from '@core/classes/DocumentAssociation';
	import PlaidLinkConfig from '@core/classes/Plaid/PlaidLinkConfig';
	import { PlaidStatementsConfig } from '@core/classes/Plaid/PlaidStatements';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import PlaidLink from '@core/components/PlaidLink.vue';
	import PlaidAccountSelect from '@core/components/PlaidAccountSelect.vue';

	export default {
		name: 'CustomerChecklistPlaidLink',
		emits: {
			uploadComplete() {
				return true;
			},
			exitItem() {
				return true;
			},
			error(errorMessage) {
				return typeof errorMessage === 'string';
			},
		},
		props: {
			dealId: String,
			storeName: String,
			storeCode: String, // TODO: If we ever start using this again, make sure this value is getting passed in.
			checklistItem: DealChecklistItem,
			receivedRedirectUrl: String,
		},
		data() {
			return {
				plaidCustomerInfo: null,
				isPlaidBusy: false,
				isBusy: false,
				selectedAccounts: [],
				isAccountSelectDone: false,
			};
		},
		computed: {
			plaidConfig() {
				const plaidConfig = new PlaidLinkConfig({
					associations: this.associations,
					name: this.storeName,
					productsToInit: this.productList,
				});

				if (this.initWithIDV) {
					plaidConfig.templateId = this.getMetadataValue('templateID') ?? null;
				} else if (this.initWithBankIncome) {
					const daysRequested = this.getMetadataValue('daysRequested') ?? 120;

					plaidConfig.incomeVerification = {
						incomeSourceTypes: [ENUMS.PLAID_INCOME_TYPE.Bank],
						bankIncome: { daysRequested: daysRequested },
					};
				} else if (this.initWithPayrollIncome) {
					plaidConfig.incomeVerification = {
						incomeSourceTypes: [ENUMS.PLAID_INCOME_TYPE.Payroll],
					};
				} else if (this.initWithStatements) {
					const oneMonthAgo = new Date();
					oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

					const startDate = this.getMetadataValue('startDate') ?? oneMonthAgo;
					const endDate = this.getMetadataValue('endDate') ?? new Date();

					plaidConfig.statements = new PlaidStatementsConfig({
						startDate,
						endDate,
					});
				}

				return plaidConfig;
			},
			associations() {
				return this.checklistItem.associations.map(a => new DocumentAssociation(a));
			},
			customerId() {
				const association = this.checklistItem.associations.find(a => {
					return a.type === ENUMS.DOC_ASSOCIATION.CustomerApplication || a.type === ENUMS.DOC_ASSOCIATION.Customer;
				});

				return association.id;
			},
			useAccountSelect() {
				return this.initWithAssets || this.initWithStatements;
			},
			useAccountMultiSelect() {
				return this.initWithAssets;
			},
			initWithIDV() {
				return this.checklistItem.type === ENUMS.DealChecklistType.LaunchPlaid_IDV;
			},
			initWithBankIncome() {
				return this.checklistItem.type === ENUMS.DealChecklistType.LaunchPlaid_BankIncome;
			},
			initWithPayrollIncome() {
				return this.checklistItem.type === ENUMS.DealChecklistType.LaunchPlaid_PayrollIncome;
			},
			initWithAssets() {
				return this.checklistItem.type === ENUMS.DealChecklistType.LaunchPlaid_AssetReport;
			},
			initWithStatements() {
				return this.checklistItem.type === ENUMS.DealChecklistType.LaunchPlaid_Statements;
			},
			productList() {
				if (this.initWithIDV) return [ENUMS.PLAID_PRODUCTS.IdentityVerification];
				else if (this.initWithAssets) return [ENUMS.PLAID_PRODUCTS.Assets];
				else if (this.initWithStatements) return [ENUMS.PLAID_PRODUCTS.Statements];
				return [ENUMS.PLAID_PRODUCTS.IncomeVerification];
			},
			successMessage() {
				if (this.initWithIDV) return 'Your identity has been successfully verified.';
				else if (this.initWithAssets) return 'Your assets are successfully submitted.';
				return 'Your income has been successfully verified.';
			},
			failureMessage() {
				if (this.initWIthIDV) return 'Your identity could not be verified. Please contact your FI Manager for more information.';
				return 'You were not able to be verified. Please contact your FI Manager for more information.';
			},
			resetWarning() {
				if (!this.initWithIDV || !this.checklistItem.status.plaidData) return null;
				return 'If you are having trouble starting our identity verification process, please ask your FI Manager to reset your session.';
			},
		},
		created() {
			localStorage.setItem('plaid_checklist_session', this.checklistItem.id);
		},
		beforeUnmount() {
			localStorage.removeItem('plaid_checklist_session');
		},
		methods: {
			async onUploadComplete(linkSuccess) {
				if (!linkSuccess) return;

				if (this.initWithBankIncome) {
					await api.plaid.getIncomeSessions(this.customerId, this.storeCode, this.checklistItem.associations);
					await api.plaid.getBankIncome(this.customerId, 1, this.storeCode, this.checklistItem.associations);
				} else if (this.initWithPayrollIncome) {
					await api.plaid.getPayrollIncome(this.customerId, this.storeCode, this.checklistItem.associations);
				}

				this.$emit('uploadComplete');
			},
			onPlaidBusyUpdate(currentState) {
				this.isPlaidBusy = currentState;
			},
			onPlaidExit(error, metadata) {
				if (!error) return;
				this.onPlaidError(error.errorCode, error.errorMessage);
			},
			onPlaidError(errCode, errMessage) {
				console.error(`${errCode}: ${errMessage}`);
				this.$emit('error', 'An error occurred while trying to launch Plaid Link.');
			},
			plaidLinkCustomerUpdate(newVal) {
				this.plaidCustomerInfo = newVal;
			},
			getMetadataValue(key) {
				if (!this.checklistItem.metadata || !Array.isArray(this.checklistItem.metadata)) return null;

				return this.checklistItem.metadata.find(d => d.key === key)?.value;
			},
			async launchLinkInUpdateMode(accessToken) {
				if (!this.$refs.plaidLink) return;
				await this.$refs.plaidLink.initLink(accessToken.plaidItemId);
			},
			selectedAccountUpdate(selectedAccounts) {
				this.selectedAccounts = selectedAccounts;
			},
			async onAccountsSelected() {
				this.isBusy = true;

				try {
					let apiSuccess = false;
					if (this.initWithAssets) apiSuccess = await this.createAssetReport();
					else if (this.initWithStatements) apiSuccess = await this.getBankStatements();

					if (apiSuccess) this.isAccountSelectDone = true;
				} catch (err) {
					console.error('SERVER ERROR: ', err);
					this.$emit('error', 'A server error occurred. Your data was unable to be uploaded. Please try again later.');
				} finally {
					this.isBusy = false;
				}
			},
			async createAssetReport() {
				if (this.selectedAccounts.length < 1) return;

				const daysRequested = this.getMetadataValue('daysRequested') ?? 120;
				const plaidItemIds = this.selectedAccounts.map(a => a.plaidItemId);

				const response = await api.plaid.createAssetReport(this.customerId, plaidItemIds, { daysRequested }, this.storeCode, this.checklistItem.associations);
				return response?.data !== null;
			},
			async getBankStatements() {
				if (this.selectedAccounts.length < 1) return;

				const plaidItemId = this.selectedAccounts[0].plaidItemId;

				const response = await api.plaid.saveAllBankStatements(this.customerId, plaidItemId, this.checklistItem.associations);
				return response?.data !== null;
			},
		},
		components: {
			LoaderComponent,
			PlaidLink,
			PlaidAccountSelect,
		},
	};
</script>
<style>
	.deal-checklist-plaid-link {
		position: relative;
	}

	.deal-checklist-plaid-link .spinner-container {
		position: absolute;
	}

	.deal-checklist-plaid-link .plaid-link header {
		display: block;
		position: relative;
		text-align: left;
		text-transform: unset;
	}

	.deal-checklist-plaid-link .plaid-link header p {
		margin: 10px 0;
	}

	.deal-checklist-plaid-link .plaid-link .account-access-warning {
		padding: 5px;
		border-radius: 10px;
		background-color: var(--error-color);
		font-weight: bold;
	}

	.deal-checklist-plaid-link .plaid-link > .plaid-launch > button {
		display: block;
		padding: 10px;
		margin: 10px 0;
		min-width: 100px;
		border: none;
		border-radius: 8px;
		height: auto;
	}

	.deal-checklist-plaid-link .Accordion {
		border-radius: 10px;
		margin: 10px 0;
	}

	.deal-checklist-plaid-link .Accordion .accordion-header {
		padding: 10px;
		border-top: unset;
		background-color: transparent;
	}

	.deal-checklist-plaid-link .Accordion .accordion-header .accordion-icon {
		transition: all 0.3s ease-in-out;
	}

	.deal-checklist-plaid-link .Accordion .accordion-header .accordion-label {
		max-width: 50%;
		overflow-wrap: break-word;
		font-weight: bold;
	}

	.deal-checklist-plaid-link .Accordion .accordion-header .connected-bank-header-items {
		margin: 5px;
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 5px;
	}

	.deal-checklist-plaid-link .Accordion .accordion-header .connected-bank-header-items button {
		padding: 5px 3px;
		border: none;
		border-radius: 10px;
		color: var(--alt-text-color);
		font-weight: bold;
	}

	.deal-checklist-plaid-link .Accordion .accordion-header .connected-bank-header-items button.update-item-button {
		background-color: var(--link-alt-text-color);
	}

	.deal-checklist-plaid-link .Accordion .accordion-header .connected-bank-header-items button.remove-item-button {
		background-color: var(--error-button-color);
	}

	.deal-checklist-plaid-link .Accordion .accordion-header .connected-bank-header-items button.login-needed {
		background-color: var(--print-color);
	}

	.deal-checklist-plaid-link .Accordion .account-list {
		display: flex;
		flex-wrap: wrap;
	}

	.deal-checklist-plaid-link .Accordion .account-list > * {
		flex: 50%;
		margin: 10px 0;
		text-transform: capitalize;
	}

	.deal-checklist-plaid-link .Accordion .account-list span b {
		display: block;
	}

	.deal-checklist-plaid-link > .plaid-link .plaid-launch,
	.deal-checklist-plaid-link .plaid-link .success-screen,
	.deal-checklist-plaid-link .plaid-link .failure-screen {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		justify-content: space-between;
	}

	.deal-checklist-plaid-link > .plaid-link .success-screen,
	.deal-checklist-plaid-link > .plaid-link .failure-screen {
		text-align: center;
	}

	.deal-checklist-plaid-link > .plaid-link i.fa-check,
	.deal-checklist-plaid-link > .plaid-link i.fa-times {
		margin: 10px;
		font-size: 100px;
	}

	.deal-checklist-plaid-link > .plaid-link i.fa-check {
		color: var(--success-button-color);
	}

	.deal-checklist-plaid-link > .plaid-link i.fa-times {
		color: var(--error-button-color);
	}

	.deal-checklist-plaid-link > .plaid-link .plaid-launch > button,
	.deal-checklist-plaid-link .plaid-link .plaid-launch > .success-screen button .deal-checklist-plaid-link .plaid-link .plaid-launch > .failure-screen button {
		width: 100%;
		padding: 10px;
		background-color: var(--success-button-color);
		color: var(--success-button-text-color);
		font-size: 1.5em;
		font-weight: bold;
	}

	.deal-checklist-plaid-link > .plaid-link .plaid-launch > .success-screen button,
	.deal-checklist-plaid-link > .plaid-link .plaid-launch > .failure-screen button {
		background-color: var(--accept-color);
	}

	.deal-checklist-plaid-link .plaid-account-select-container {
		display: flex;
		justify-content: space-between;
	}

	.deal-checklist-plaid-link .plaid-account-select.bank-select .multi-select-list,
	.deal-checklist-plaid-link .plaid-account-select.bank-select .single-select-list {
		width: 100%;
	}

	.deal-checklist-plaid-link .plaid-account-select.bank-select fieldset {
		padding: 10px;
		margin: 10px 0;
		border: 1px solid var(--border-color);
		border-radius: 10px;
		font-weight: bold;
		transition: all 0.3s ease-in-out;
	}

	.deal-checklist-plaid-link .plaid-account-select.bank-select fieldset.selected {
		border: 2px solid var(--accept-color);
	}

	.deal-checklist-plaid-link .plaid-account-select.bank-select fieldset label {
		width: 100%;
		display: block;
	}

	.deal-checklist-plaid-link .plaid-account-select-container .plaid-link .launch-link-button {
		width: fit-content;
		background-color: var(--accept-color);
		color: var(--alt-text-color);
		font-weight: bold;
	}

	.deal-checklist-plaid-link .plaid-account-select-container .button-account-select {
		width: 100%;
		padding: 10px;
		background-color: var(--success-button-color);
		color: var(--success-button-text-color);
		font-size: 1.5em;
		font-weight: bold;
	}

	.deal-checklist-plaid-link .plaid-account-select-post {
		justify-content: space-between;
		text-align: center;
	}

	.deal-checklist-plaid-link .plaid-account-select-post i.fa-check {
		margin: 10px;
		font-size: 100px;
		color: var(--success-button-color);
	}

	.deal-checklist-plaid-link .plaid-account-select-post button {
		width: 100%;
		padding: 10px;
		background-color: var(--accept-color);
		color: var(--success-button-text-color);
		font-size: 1.5em;
		font-weight: bold;
	}
</style>
